import { useEffect, useState } from 'react';
import Header from '@/components/Header';
import api from '@/lib/api';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Monitor, Copy, XCircle, Trash2, AlertTriangle, Tv } from 'lucide-react';
import { useSeo } from '@/lib/seo';

dayjs.extend(relativeTime);

const ONLINE_WINDOW_MIN = 3; // a wall TV heartbeats via its API polls; seen within 3 min = online

function screenHealth(s) {
  if (!s.active) return { state: 'revoked', color: 'bg-zinc-300', text: 'Revoked' };
  if (!s.last_seen) return { state: 'never', color: 'bg-zinc-300', text: 'Never connected' };
  const mins = dayjs().diff(dayjs(s.last_seen), 'minute');
  if (mins <= ONLINE_WINDOW_MIN) return { state: 'online', color: 'bg-emerald-500', text: 'Online now' };
  return { state: 'offline', color: 'bg-red-500', text: `Offline · last seen ${dayjs(s.last_seen).fromNow()}` };
}

function screenExpiry(s) {
  if (!s.created_at || !s.days) return null;
  const exp = dayjs(s.created_at).add(s.days, 'day');
  return { exp, daysLeft: Math.ceil(exp.diff(dayjs(), 'hour') / 24), expired: exp.isBefore(dayjs()) };
}

function LinkRow({ label, url, onCopy, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-zinc-500 w-28 shrink-0">{label}</span>
      <input readOnly value={url} data-testid={testid} onFocus={(e) => e.target.select()} className="flex-1 border border-zinc-300 rounded-lg px-3 py-2 text-xs font-mono bg-zinc-50" />
      <button onClick={() => onCopy(url)} className="btn btn-ghost shrink-0" data-testid={`${testid}-copy`} title="Copy link">
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function NocScreens() {
  useSeo({ title: 'NOC & CCTV wall screens', description: 'Set up and manage sign-in links for unattended NOC / CCTV wall TVs.' });
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(365);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [pairLabel, setPairLabel] = useState('');
  const [approving, setApproving] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const load = async () => {
    try { const { data } = await api.get('/noc/screens'); setScreens(data || []); } catch { /* noop */ }
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const create = async () => {
    setLoading(true);
    try {
      await api.post('/noc/screens', { label: label || 'Screen', days: parseInt(days, 10) || 365 });
      setLabel('');
      toast.success('Screen link created');
      load();
    } catch { toast.error('Could not create link'); }
    finally { setLoading(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this link? The TV using it will be signed out.')) return;
    try { await api.post(`/noc/screens/${id}/revoke`); toast.success('Link revoked'); load(); }
    catch { toast.error('Revoke failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this screen link permanently?')) return;
    try { await api.delete(`/noc/screens/${id}`); load(); }
    catch { toast.error('Delete failed'); }
  };

  const approve = async () => {
    const c = code.replace(/\D/g, '');
    if (c.length !== 6) { toast.error('Enter the 6-digit code shown on the screen'); return; }
    setApproving(true);
    try {
      await api.post('/noc/pair/approve', { code: c, label: pairLabel || 'Paired screen', days: parseInt(days, 10) || 365 });
      setCode(''); setPairLabel('');
      toast.success('Screen approved — it will connect shortly');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve code');
    } finally { setApproving(false); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Link copied'),
      () => toast.error('Copy failed — select and copy manually')
    );
  };

  const anySoon = screens.some((s) => { const e = s.active && screenExpiry(s); return e && e.daysLeft <= 14; });

  return (
    <>
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-4" data-testid="noc-screens-page">
        <div>
          <h1 className="font-display font-black text-2xl flex items-center gap-2"><Monitor className="w-6 h-6 text-teal-600" /> NOC &amp; CCTV wall screens</h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Sign an unattended wall TV into your gated NOC, CCTV &amp; VMS screens <strong>without the login form</strong>.
            Create a link and open it on the TV, or approve the 6-digit code the TV shows. Every link is a
            read-only Business account you can revoke any time.
          </p>
        </div>

        <div className="card">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-zinc-200 p-3">
              <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Tv className="w-4 h-4 text-teal-600" /> Create a link</div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-sm flex-1 min-w-[8rem]">
                  <span className="block text-zinc-500 mb-1">Name</span>
                  <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Front NOC TV" data-testid="screens-label" className="w-full border border-zinc-300 rounded-lg px-3 py-2" />
                </label>
                <label className="text-sm">
                  <span className="block text-zinc-500 mb-1">Days</span>
                  <input type="number" min="1" max="3650" value={days} onChange={(e) => setDays(e.target.value)} data-testid="screens-days" className="w-24 border border-zinc-300 rounded-lg px-3 py-2" />
                </label>
                <button onClick={create} disabled={loading} data-testid="screens-generate" className="btn btn-primary">
                  {loading ? 'Creating…' : 'Create'}
                </button>
              </div>
            </div>
            <div className="rounded-lg border border-zinc-200 p-3">
              <div className="font-semibold text-sm mb-2">Approve a screen code</div>
              <p className="text-xs text-zinc-500 mb-2">Open the NOC, CCTV or VMS wall on the TV — it shows a 6-digit code. Enter it here.</p>
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-sm">
                  <span className="block text-zinc-500 mb-1">6-digit code</span>
                  <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={7} placeholder="000000" data-testid="screens-pair-code" className="w-28 border border-zinc-300 rounded-lg px-3 py-2 font-mono tracking-widest" />
                </label>
                <label className="text-sm flex-1 min-w-[8rem]">
                  <span className="block text-zinc-500 mb-1">Name (optional)</span>
                  <input value={pairLabel} onChange={(e) => setPairLabel(e.target.value)} placeholder="e.g. Ops room wall" data-testid="screens-pair-label" className="w-full border border-zinc-300 rounded-lg px-3 py-2" />
                </label>
                <button onClick={approve} disabled={approving} data-testid="screens-pair-approve" className="btn btn-primary">
                  {approving ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card" data-testid="screens-list">
          <div className="font-semibold text-sm mb-3">Your screen links ({screens.length})</div>
          {anySoon && (
            <div className="mb-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="screens-expiry-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Some links expire within 14 days — create a fresh link and open it on the TV before the old one lapses, so walls never drop offline.</span>
            </div>
          )}
          {screens.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">No screen links yet.</div>}
          <div className="space-y-3">
            {screens.map((s) => {
              const e = screenExpiry(s);
              const h = screenHealth(s);
              return (
                <div key={s.id} className="rounded-lg border border-zinc-200 p-3" data-testid={`screen-${s.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${h.color} ${h.state === 'online' ? 'animate-pulse ring-2 ring-emerald-200' : ''}`}
                          title={h.text}
                          data-testid={`screen-health-${s.id}`}
                          data-health={h.state}
                        />
                        <span className="font-semibold text-sm">{s.label || 'Screen'}</span>
                      </span>
                      <span className={`ml-2 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${s.active ? 'bg-teal-100 text-teal-700' : 'bg-zinc-200 text-zinc-500'}`}>
                        {s.active ? 'Active' : 'Revoked'}
                      </span>
                      <span className="ml-2 text-xs text-zinc-400">created {s.created_at ? dayjs(s.created_at).format('D MMM YYYY') : '—'}</span>
                      {s.active && e && (
                        <span className={`ml-2 text-xs font-semibold ${e.expired ? 'text-red-600' : e.daysLeft <= 14 ? 'text-amber-600' : 'text-zinc-400'}`} data-testid={`screen-expiry-${s.id}`}>
                          {e.expired ? 'expired' : `expires in ${e.daysLeft}d`}
                        </span>
                      )}
                      {s.active && (
                        <span className={`ml-2 text-xs font-semibold ${h.state === 'online' ? 'text-emerald-600' : h.state === 'offline' ? 'text-red-600' : 'text-zinc-400'}`} data-testid={`screen-health-text-${s.id}`}>
                          · {h.text}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {s.active && <button onClick={() => revoke(s.id)} className="btn btn-ghost text-red-600" data-testid={`screen-revoke-${s.id}`} title="Revoke"><XCircle className="w-4 h-4" /></button>}
                      <button onClick={() => del(s.id)} className="btn btn-ghost" data-testid={`screen-delete-${s.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {s.active && (
                    <div className="space-y-2">
                      <LinkRow label="CCTV wall" url={origin + s.paths.cctv} onCopy={copy} testid={`screen-link-cctv-${s.id}`} />
                      {s.paths.vms && <LinkRow label="VMS wall" url={origin + s.paths.vms} onCopy={copy} testid={`screen-link-vms-${s.id}`} />}
                      <LinkRow label="NOC overview" url={origin + s.paths.noc} onCopy={copy} testid={`screen-link-noc-${s.id}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
